import React from 'react';
import imageCompression from 'browser-image-compression';
import { Upload } from 'tus-js-client';

import useIsMounted from '@src/hooks/useIsMounted';

import {
  getFileExtension,
  getMimeType,
  getUploadEndpoint,
  urlToFile,
} from './helpers';
import { MediaType } from './types';

export async function webImageCompressor(
  file: File,
  maxSizeMB: number = 1,
  onProgress: (progress: number) => void
): Promise<File> {
  const options = {
    maxSizeMB,
    onProgress: (progress: number) => {
      onProgress(progress);
    },
    useWebWorker: true,
  };

  return imageCompression(file, options);
}

const useMediaUploader = () => {
  const isMounted = useIsMounted();
  const [uploadUrl, setUploadUrl] = React.useState<string>();
  const [completed, setCompleted] = React.useState(false);
  const [error, setError] = React.useState<string>();
  const [progress, setProgress] = React.useState<string>();
  const [compressionProgress, setCompressionProgress] = React.useState(0);

  const upload = React.useCallback(
    async (
      mediaUri: string,
      mediaType: MediaType,
      onSuccessCallback: (uploadUrl: string) => void
    ) => {
      const extension = getFileExtension(mediaUri);
      const endpoint = getUploadEndpoint(mediaType);
      const filename = `photo.${extension}`;
      const mimeType = getMimeType(extension);

      const file = await urlToFile(mediaUri, filename, mimeType);
      const compressedImage = await webImageCompressor(file, 1, (p) => {
        setCompressionProgress(p);
      });

      const tusUpload = new Upload(compressedImage, {
        endpoint,
        chunkSize: 0.5 * 1024 * 1024,
        metadata: {
          filename: `photo.${extension}`,
          filetype: mimeType,
        },
        onError: (_error: any) => {
          if (isMounted) {
            console.log(`Error in upload file: ${_error}`);
            setError(`Error in upload file: ${_error}`);
          }
        },
        onProgress: (uploadedBytes: number, totalBytes: number) => {
          const percentage = ((uploadedBytes / totalBytes) * 100).toFixed(2);
          if (isMounted) {
            setProgress(percentage);
          }
        },
        onSuccess: () => {
          tusUpload?.url && setUploadUrl(tusUpload.url);
          tusUpload?.url && onSuccessCallback(tusUpload.url);
          if (isMounted) {
            setCompleted(true);
          }
        },
      });
      setCompleted(false);
      tusUpload.start();
    },
    [isMounted]
  );

  return React.useMemo(
    () => ({
      progress,
      error,
      uploadUrl,
      upload,
      completed,
      compressionProgress,
    }),
    [completed, compressionProgress, error, progress, upload, uploadUrl]
  );
};

export default useMediaUploader;
